import { DocumentStorageAccessError } from '@/lib/documentStorageAccess';

/** Bytes stay as stored. The blob type tells the browser the text is UTF-8. */
export function attachmentContentType(mimeType: string, filename: string): string {
  const base = (mimeType || '').split(';')[0]?.trim().toLowerCase() ?? '';
  const ext = filename.includes('.') ? (filename.split('.').pop()?.toLowerCase() ?? '') : '';
  if (base === 'text/plain' || ext === 'txt' || ext === 'log' || ext === 'md') {
    return 'text/plain;charset=utf-8';
  }
  return base || 'application/octet-stream';
}

/**
 * Header value for a download that keeps spaces and æøå.
 * `filename` is an ASCII fallback; `filename*` is the original UTF-8 name.
 */
export function contentDispositionAttachment(filename: string): string {
  const cleaned = filename.replace(/[\r\n"]/g, '_').trim() || 'fil';
  const ascii = cleaned.replace(/[^\u0020-\u007E]/g, '_');
  const star = encodeURIComponent(cleaned);
  return `attachment; filename="${ascii}"; filename*=UTF-8''${star}`;
}

export function saveBlobAsFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function downloadBytesAsFile(input: {
  bytes: ArrayBuffer;
  mimeType: string;
  filename: string;
}): Promise<void> {
  const blob = new Blob([input.bytes], {
    type: attachmentContentType(input.mimeType, input.filename),
  });
  saveBlobAsFile(blob, input.filename);
}

export async function downloadUrlAsFile(input: {
  url: string;
  mimeType: string;
  filename: string;
}): Promise<void> {
  const response = await fetch(input.url);
  if (!response.ok) {
    if (response.status === 400 || response.status === 404) throw new DocumentStorageAccessError();
    throw new Error('download_failed');
  }
  await downloadBytesAsFile({
    bytes: await response.arrayBuffer(),
    mimeType: input.mimeType,
    filename: input.filename,
  });
}
